import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Save, ArrowLeft, Upload, FileText, Trash2, FileDown, Printer, Receipt, Award, FileSignature } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id) && id !== 'new';
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [showSoldeDialog, setShowSoldeDialog] = useState(false);
  const [soldeAmount, setSoldeAmount] = useState('');
  const [generatingSolde, setGeneratingSolde] = useState(false);
  const [generatingAttestation, setGeneratingAttestation] = useState(false);
  const [generatingContrat, setGeneratingContrat] = useState(false);
  
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    gender: 'm',
    id_card_number: '',
    id_card_issue_date: '',
    social_security_number: '',
    birth_date: '',
    address: '',
    phone: '',
    email: '',
    contract_type: 'vollzeit',
    start_date: '',
    end_date: '',
    position: '',
    department: '',
    work_hours_per_week: 40,
    standard_daily_hours: 8,
    hourly_rate: 0,
    monthly_fixed_salary: '',
    standard_bonus: 0,
    overtime_surcharge_percent: 25,
    night_surcharge_percent: 0,
    holiday_surcharge_percent: 0,
    weekend_surcharge_percent: 0,
    other_surcharge_percent: 0,
    currency: 'TND',
    vacation_days_per_year: 20,
    vacation_days_taken: 0,
    is_active: true,
    termination_date: '',
    bank_name: '',
    bank_iban: '',
    bank_bic: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await axios.get(`${API}/employees/${id}`);
        const emp = response.data;
        setForm({
          ...emp,
          gender: emp.gender || 'm',
          id_card_issue_date: emp.id_card_issue_date || '',
          monthly_fixed_salary: emp.monthly_fixed_salary || '',
          end_date: emp.end_date || '',
          termination_date: emp.termination_date || '',
          bank_name: emp.bank_name || '',
          bank_iban: emp.bank_iban || '',
          bank_bic: emp.bank_bic || '',
          work_hours_per_week: emp.work_hours_per_week || 40
        });
      } catch (error) {
        console.error('Error fetching employee:', error);
        toast.error(t('errorOccurred'));
        navigate('/employees');
      } finally {
        setLoading(false);
      }
    };

    const fetchDocuments = async () => {
      try {
        const response = await axios.get(`${API}/documents/${id}`);
        setDocuments(response.data);
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };

    if (isEdit) {
      fetchEmployee();
      fetchDocuments();
    }
  }, [id, isEdit, navigate, t]);

  const refetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleDownloadResignationLetter = async () => {
    if (!id) return;
    setGeneratingLetter(true);
    try {
      const response = await axios.get(`${API}/export/resignation-letter/${id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Lettre_Demission_${form.last_name}_${form.first_name}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('savedSuccessfully'));
    } catch (error) {
      console.error('Error generating resignation letter:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleGenerateSoldeToutCompte = async () => {
    if (!id || !soldeAmount) return;
    
    const amount = parseFloat(soldeAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }
    
    setGeneratingSolde(true);
    try {
      const response = await axios.post(`${API}/export/solde-tout-compte/${id}`, 
        { amount },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Solde_Tout_Compte_${form.last_name}_${form.first_name}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowSoldeDialog(false);
      setSoldeAmount('');
      toast.success(t('savedSuccessfully'));
    } catch (error) {
      console.error('Error generating solde tout compte:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingSolde(false);
    }
  };

  const handlePrintSoldeToutCompte = async () => {
    if (!id || !soldeAmount) return;
    
    const amount = parseFloat(soldeAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }
    
    setGeneratingSolde(true);
    try {
      const response = await axios.post(`${API}/export/solde-tout-compte/${id}`, 
        { amount },
        { responseType: 'blob' }
      );
      
      // Open PDF in new window for printing
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      setShowSoldeDialog(false);
      setSoldeAmount('');
    } catch (error) {
      console.error('Error generating solde tout compte for print:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingSolde(false);
    }
  };

  const handleDownloadAttestation = async () => {
    if (!id) return;
    setGeneratingAttestation(true);
    try {
      const response = await axios.get(`${API}/export/attestation-travail/${id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Attestation_Travail_${form.last_name}_${form.first_name}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('savedSuccessfully'));
    } catch (error) {
      console.error('Error generating attestation:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingAttestation(false);
    }
  };

  const handlePrintAttestation = async () => {
    if (!id) return;
    setGeneratingAttestation(true);
    try {
      const response = await axios.get(`${API}/export/attestation-travail/${id}`, {
        responseType: 'blob'
      });
      
      // Open PDF in new window for printing
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Error generating attestation for print:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingAttestation(false);
    }
  };

  const handleDownloadContratFreelance = async () => {
    if (!id) return;
    setGeneratingContrat(true);
    try {
      const response = await axios.get(`${API}/export/contrat-freelance/${id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Contrat_Freelance_${form.last_name}_${form.first_name}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('savedSuccessfully'));
    } catch (error) {
      console.error('Error generating freelance contract:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingContrat(false);
    }
  };

  const handlePrintContratFreelance = async () => {
    if (!id) return;
    setGeneratingContrat(true);
    try {
      const response = await axios.get(`${API}/export/contrat-freelance/${id}`, {
        responseType: 'blob'
      });
      
      // Open PDF in new window for printing
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Error generating freelance contract for print:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setGeneratingContrat(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setValidationErrors({});

    // Validate required fields
    const requiredFields = {
      first_name: t('firstName'),
      last_name: t('lastName'),
      id_card_number: t('idCardNumber'),
      social_security_number: t('socialSecurityNumber'),
      birth_date: t('birthDate'),
      start_date: t('startDate'),
      position: t('position')
    };

    const errors = {};
    const missingFields = [];

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!form[field] || form[field].trim() === '') {
        errors[field] = true;
        missingFields.push(label);
      }
    }

    if (missingFields.length > 0) {
      setValidationErrors(errors);
      toast.error(`${t('requiredFieldsMissing')}: ${missingFields.join(', ')}`);
      setSaving(false);
      return;
    }

    try {
      const data = {
        ...form,
        id_card_issue_date: form.id_card_issue_date || null,
        monthly_fixed_salary: form.monthly_fixed_salary || null,
        end_date: form.end_date || null,
        termination_date: form.termination_date || null,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        work_hours_per_week: parseFloat(form.work_hours_per_week) || 40,
        standard_daily_hours: parseFloat(form.standard_daily_hours) || 8,
        standard_bonus: parseFloat(form.standard_bonus) || 0,
        overtime_surcharge_percent: parseFloat(form.overtime_surcharge_percent) || 25,
        night_surcharge_percent: parseFloat(form.night_surcharge_percent) || 0,
        holiday_surcharge_percent: parseFloat(form.holiday_surcharge_percent) || 0,
        weekend_surcharge_percent: parseFloat(form.weekend_surcharge_percent) || 0,
        other_surcharge_percent: parseFloat(form.other_surcharge_percent) || 0,
        vacation_days_per_year: parseInt(form.vacation_days_per_year) || 20,
        vacation_days_taken: parseInt(form.vacation_days_taken) || 0
      };

      if (isEdit) {
        await axios.put(`${API}/employees/${id}`, data);
      } else {
        await axios.post(`${API}/employees`, data);
      }
      
      toast.success(t('savedSuccessfully'));
      navigate('/employees');
    } catch (error) {
      console.error('Error saving employee:', error);
      // Handle Pydantic validation errors (array of objects)
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/documents/upload?employee_id=${id}&document_type=${docType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(t('savedSuccessfully'));
      refetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setUploadingDoc(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/employees')}
          className="text-gray-500 hover:text-[#1A1A1A]"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-3xl font-bold text-[#1A1A1A]">
          {isEdit ? t('editEmployee') : t('newEmployee')}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="personal" className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="personal" className="data-[state=active]:bg-[#E31E24] data-[state=active]:text-[#1A1A1A]">
              {t('personalData')}
            </TabsTrigger>
            <TabsTrigger value="contract" className="data-[state=active]:bg-[#E31E24] data-[state=active]:text-[#1A1A1A]">
              {t('contractData')}
            </TabsTrigger>
            <TabsTrigger value="salary" className="data-[state=active]:bg-[#E31E24] data-[state=active]:text-[#1A1A1A]">
              {t('salaryData')}
            </TabsTrigger>
            <TabsTrigger value="vacation" className="data-[state=active]:bg-[#E31E24] data-[state=active]:text-[#1A1A1A]">
              {t('vacationManagement')}
            </TabsTrigger>
            {isEdit && (
              <TabsTrigger value="documents" className="data-[state=active]:bg-[#E31E24] data-[state=active]:text-[#1A1A1A]">
                {t('documents')}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Personal Data */}
          <TabsContent value="personal">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">{t('personalData')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('firstName')} *</Label>
                    <Input
                      value={form.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      required
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.first_name ? 'border-red-500' : ''}`}
                      data-testid="input-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('lastName')} *</Label>
                    <Input
                      value={form.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      required
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.last_name ? 'border-red-500' : ''}`}
                      data-testid="input-last-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('gender')} *</Label>
                    <Select value={form.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]" data-testid="select-gender">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="m">{t('male')}</SelectItem>
                        <SelectItem value="w">{t('female')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('idCardNumber')} *</Label>
                    <Input
                      value={form.id_card_number}
                      onChange={(e) => handleChange('id_card_number', e.target.value)}
                      required
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.id_card_number ? 'border-red-500' : ''}`}
                      data-testid="input-id-card"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('idCardIssueDate')}</Label>
                    <Input
                      type="date"
                      value={form.id_card_issue_date}
                      onChange={(e) => handleChange('id_card_issue_date', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-id-card-issue-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('socialSecurityNumber')} *</Label>
                    <Input
                      value={form.social_security_number}
                      onChange={(e) => handleChange('social_security_number', e.target.value)}
                      required
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.social_security_number ? 'border-red-500' : ''}`}
                      data-testid="input-ssn"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('birthDate')} *</Label>
                    <Input
                      type="date"
                      value={form.birth_date}
                      onChange={(e) => handleChange('birth_date', e.target.value)}
                      required
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.birth_date ? 'border-red-500' : ''}`}
                      data-testid="input-birth-date"
                    />
                    {validationErrors.birth_date && (
                      <span className="text-xs text-red-500">{t('requiredFieldsMissing')}</span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('email')}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('phone')}</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-gray-500">{t('address')}</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-address"
                    />
                  </div>
                </div>
                
                {/* Bankdaten */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-[#1A1A1A] mb-4">{t('bankDetails')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-500">{t('bankName')}</Label>
                      <Input
                        value={form.bank_name}
                        onChange={(e) => handleChange('bank_name', e.target.value)}
                        placeholder="z.B. BIAT, Attijari Bank"
                        className="bg-white border-gray-200 text-[#1A1A1A]"
                        data-testid="input-bank-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-500">{t('bankIban')}</Label>
                      <Input
                        value={form.bank_iban}
                        onChange={(e) => handleChange('bank_iban', e.target.value)}
                        placeholder="TN59 1234 5678 9012 3456 7890"
                        className="bg-white border-gray-200 text-[#1A1A1A]"
                        data-testid="input-bank-iban"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-500">{t('bankBic')}</Label>
                      <Input
                        value={form.bank_bic}
                        onChange={(e) => handleChange('bank_bic', e.target.value)}
                        placeholder="RIB / BIC / SWIFT"
                        className="bg-white border-gray-200 text-[#1A1A1A]"
                        data-testid="input-bank-bic"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contract Data */}
          <TabsContent value="contract">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">{t('contractData')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('position')} *</Label>
                    <Input
                      value={form.position}
                      onChange={(e) => handleChange('position', e.target.value)}
                      required
                      placeholder="z.B. Entwickler, Manager, Buchhalter"
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.position ? 'border-red-500' : ''}`}
                      data-testid="input-position"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('department')}</Label>
                    <Input
                      value={form.department}
                      onChange={(e) => handleChange('department', e.target.value)}
                      placeholder="z.B. IT, Finanzen, Personal"
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-department"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('contractType')} *</Label>
                    <Select value={form.contract_type} onValueChange={(v) => handleChange('contract_type', v)}>
                      <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]" data-testid="select-contract-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="vollzeit">{t('fulltime')}</SelectItem>
                        <SelectItem value="teilzeit">{t('parttime')}</SelectItem>
                        <SelectItem value="freelancer">{t('freelancer')}</SelectItem>
                        <SelectItem value="minijob">{t('minijob')}</SelectItem>
                        <SelectItem value="befristet">{t('temporary')}</SelectItem>
                        <SelectItem value="sivp">SIVP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('startDate')} *</Label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => handleChange('start_date', e.target.value)}
                      required
                      className={`bg-white border-gray-200 text-[#1A1A1A] ${validationErrors.start_date ? 'border-red-500' : ''}`}
                      data-testid="input-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('endDate')}</Label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => handleChange('end_date', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-end-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('workHoursPerWeek')}</Label>
                    <Input
                      type="number"
                      value={form.work_hours_per_week}
                      onChange={(e) => handleChange('work_hours_per_week', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-hours-week"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('standardDailyHours')}</Label>
                    <Input
                      type="number"
                      value={form.standard_daily_hours}
                      onChange={(e) => handleChange('standard_daily_hours', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-daily-hours"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('terminationDate')}</Label>
                    <Input
                      type="date"
                      value={form.termination_date}
                      onChange={(e) => handleChange('termination_date', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-termination-date"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <Switch
                      checked={form.is_active}
                      onCheckedChange={(v) => handleChange('is_active', v)}
                      data-testid="switch-active"
                    />
                    <Label className="text-gray-500">{t('active')}</Label>
                  </div>
                </div>
                
                {/* Resignation Letter Button - only show for existing employees */}
                {isEdit && (
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-[#1A1A1A]">{t('resignationLetter')}</h3>
                        <p className="text-xs text-gray-500 mt-1">{t('resignationLetterTooltip')}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDownloadResignationLetter}
                        disabled={generatingLetter}
                        className="border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24]/10"
                        data-testid="btn-resignation-letter"
                      >
                        {generatingLetter ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-[#E31E24]/30 border-t-[#E31E24] rounded-full animate-spin" />
                            {t('loading')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <FileDown className="w-4 h-4" />
                            {t('resignationLetter')}
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Solde de tout compte Button - only show for existing employees */}
                {isEdit && (
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-[#1A1A1A]">{t('soldeToutCompte')}</h3>
                        <p className="text-xs text-gray-500 mt-1">{t('soldeToutCompteTooltip')}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowSoldeDialog(true)}
                        className="border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24]/10"
                        data-testid="btn-solde-tout-compte"
                      >
                        <span className="flex items-center gap-2">
                          <Receipt className="w-4 h-4" />
                          {t('soldeToutCompte')}
                        </span>
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Attestation de travail Button - only show for existing employees */}
                {isEdit && (
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-[#1A1A1A]">{t('attestationTravail')}</h3>
                        <p className="text-xs text-gray-500 mt-1">{t('attestationTravailTooltip')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handlePrintAttestation}
                          disabled={generatingAttestation}
                          className="border-gray-300 text-gray-700 hover:bg-gray-100"
                          data-testid="btn-print-attestation"
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          {t('print')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleDownloadAttestation}
                          disabled={generatingAttestation}
                          className="border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24]/10"
                          data-testid="btn-download-attestation"
                        >
                          {generatingAttestation ? (
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-[#E31E24]/30 border-t-[#E31E24] rounded-full animate-spin" />
                              {t('loading')}
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <Award className="w-4 h-4" />
                              {t('attestationTravail')}
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Contrat Freelance Button - only show for existing employees */}
                {isEdit && (
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-[#1A1A1A]">{t('contratFreelance')}</h3>
                        <p className="text-xs text-gray-500 mt-1">{t('contratFreelanceTooltip')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handlePrintContratFreelance}
                          disabled={generatingContrat}
                          className="border-gray-300 text-gray-700 hover:bg-gray-100"
                          data-testid="btn-print-contrat"
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          {t('print')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleDownloadContratFreelance}
                          disabled={generatingContrat}
                          className="border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24]/10"
                          data-testid="btn-download-contrat"
                        >
                          {generatingContrat ? (
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-[#E31E24]/30 border-t-[#E31E24] rounded-full animate-spin" />
                              {t('loading')}
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <FileSignature className="w-4 h-4" />
                              {t('contratFreelance')}
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Salary Data */}
          <TabsContent value="salary">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">{t('salaryData')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('hourlyRate')} *</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.hourly_rate}
                      onChange={(e) => handleChange('hourly_rate', e.target.value)}
                      required
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-hourly-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('monthlyFixedSalary')}</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.monthly_fixed_salary}
                      onChange={(e) => handleChange('monthly_fixed_salary', e.target.value)}
                      placeholder="Optional"
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-monthly-salary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('standardBonus')}</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.standard_bonus}
                      onChange={(e) => handleChange('standard_bonus', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-standard-bonus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('currency')}</Label>
                    <Select value={form.currency} onValueChange={(v) => handleChange('currency', v)}>
                      <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]" data-testid="select-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="TND">TND</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('overtimeSurcharge')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.overtime_surcharge_percent}
                      onChange={(e) => handleChange('overtime_surcharge_percent', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-overtime-surcharge"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('nightSurcharge')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.night_surcharge_percent}
                      onChange={(e) => handleChange('night_surcharge_percent', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-night-surcharge"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('holidaySurcharge')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.holiday_surcharge_percent}
                      onChange={(e) => handleChange('holiday_surcharge_percent', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-holiday-surcharge"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('weekendSurcharge')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.weekend_surcharge_percent}
                      onChange={(e) => handleChange('weekend_surcharge_percent', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-weekend-surcharge"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('otherSurcharge')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.other_surcharge_percent}
                      onChange={(e) => handleChange('other_surcharge_percent', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-other-surcharge"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vacation Management */}
          <TabsContent value="vacation">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#1A1A1A]">{t('vacationManagement')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('vacationDaysPerYear')}</Label>
                    <Input
                      type="number"
                      value={form.vacation_days_per_year}
                      onChange={(e) => handleChange('vacation_days_per_year', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-vacation-per-year"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('vacationDaysTaken')}</Label>
                    <Input
                      type="number"
                      value={form.vacation_days_taken}
                      onChange={(e) => handleChange('vacation_days_taken', e.target.value)}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      data-testid="input-vacation-taken"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('vacationDaysRemaining')}</Label>
                    <Input
                      type="number"
                      value={form.vacation_days_per_year - form.vacation_days_taken}
                      disabled
                      className="bg-gray-50 border-gray-200 text-[#1A1A1A] font-mono"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents */}
          {isEdit && (
            <TabsContent value="documents">
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-[#1A1A1A]">{t('documents')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {['contract', 'idCard', 'socialSecurity', 'other'].map((docType) => (
                      <div key={docType} className="space-y-2">
                        <Label className="text-gray-500">{t(docType)}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            onChange={(e) => handleDocumentUpload(e, docType)}
                            disabled={uploadingDoc}
                            className="bg-white border-gray-200 text-[#1A1A1A] file:bg-[#E31E24] file:text-[#1A1A1A] file:border-0 file:mr-4 file:py-1 file:px-3 file:rounded"
                            data-testid={`upload-${docType}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {documents.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-500 mb-3">{t('uploadDocument')}</h4>
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                            <FileText className="w-5 h-5 text-[#E31E24]" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#1A1A1A] truncate">{doc.original_filename}</p>
                              <p className="text-xs text-gray-400">{t(doc.document_type)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/employees')}
            className="border-gray-200 text-gray-500 hover:text-[#1A1A1A] hover:bg-gray-100"
            data-testid="cancel-button"
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow"
            data-testid="save-button"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('loading')}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                {t('save')}
              </span>
            )}
          </Button>
        </div>
      </form>
      
      {/* Solde de tout compte Dialog */}
      <Dialog open={showSoldeDialog} onOpenChange={setShowSoldeDialog}>
        <DialogContent className="sm:max-w-[425px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#1A1A1A]">{t('soldeToutCompteTitle')}</DialogTitle>
            <DialogDescription className="text-gray-500">
              {form.first_name} {form.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="solde-amount" className="text-gray-700">
                {t('amountDinar')} *
              </Label>
              <Input
                id="solde-amount"
                type="text"
                inputMode="decimal"
                value={soldeAmount}
                onChange={(e) => setSoldeAmount(e.target.value)}
                placeholder={t('amountPlaceholder')}
                className="bg-white border-gray-200 text-[#1A1A1A]"
                data-testid="input-solde-amount"
              />
              <p className="text-xs text-gray-400">
                Beispiel: 1500.000 = Mille cinq cents dinars
              </p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowSoldeDialog(false);
                setSoldeAmount('');
              }}
              className="border-gray-300"
            >
              {t('cancel')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handlePrintSoldeToutCompte}
              disabled={generatingSolde || !soldeAmount}
              className="border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24]/10"
              data-testid="btn-print-solde"
            >
              <Printer className="w-4 h-4 mr-2" />
              {t('print')}
            </Button>
            <Button
              type="button"
              onClick={handleGenerateSoldeToutCompte}
              disabled={generatingSolde || !soldeAmount}
              className="bg-[#E31E24] text-white hover:bg-[#E31E24]/90"
              data-testid="btn-download-solde"
            >
              {generatingSolde ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('loading')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FileDown className="w-4 h-4" />
                  Download PDF
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
